import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { SearchSelect } from '@/design-system/search-select';
import { toast } from '@/design-system/toaster';
import {
  DispatchConfig,
  DispatchDetail,
  DispatchRiderSnapshot,
  dispatchesAPI,
} from '@/services/api/dispatches';
import {
  DispatchDriver,
  dispatchDriversAPI,
  driverDisplayName,
} from '@/services/api/dispatchDrivers';
import {
  HubOption,
  PitstopType,
  searchDispatchOriginOptions,
} from '@/services/api/pitstops';
import { useSearchableOptions } from '@/hooks/useSearchableOptions';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';

interface CreateDispatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (dispatch: DispatchDetail) => void;
}

const labelCls =
  'mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600';
const inputCls =
  'w-full rounded-lg border border-stroke-soft-200 bg-white px-3 py-2 text-sm text-text-strong-950 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent';

// Which dropdown section an origin belongs to. The two-call fetcher tags each
// option with the list it came from; `hasStaff` is only a fallback, since a
// HUB-type pitstop can legitimately be unstaffed.
const isDashHub = (o: HubOption): boolean =>
  o.pitstopType != null
    ? o.pitstopType === PitstopType.HUB
    : o.hasStaff !== false;

// Fallback when GET /v1/dispatch/config is unreachable or returns no usable
// maxStopsPerBatch — mirrors the BE's own DISPATCH_CONFIG default.
const DEFAULT_MAX_STOPS_PER_BATCH = 8;

const CreateDispatchModal: React.FC<CreateDispatchModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const [hubId, setHubId] = useState<string>('');
  // Dispatch origin picker — EVERY pitstop, server-searchable: Dash hubs with
  // or without staff AND client pitstops (PROVIDER_WAREHOUSE). A dispatch can
  // depart from any of them — unscheduled client-pitstop imports in
  // particular are dispatched manually from their pitstop
  // (docs/pitstop/client-pitstop-no-schedule-v1.md), which the old
  // `type=HUB&hasStaff=true` list could never offer. The fetcher queries each
  // type on its own (HUB size=50 + PROVIDER_WAREHOUSE size=100) so neither
  // section of the dropdown can be crowded out by the other.
  const hubSearch = useSearchableOptions(isOpen, searchDispatchOriginOptions);
  // The picked origin's own metadata (staffed? owning client?). Captured at
  // pick time from the option row, because a later search can narrow the
  // picked option out of `hubSearch.options` and lose these fields.
  const [selectedHubMeta, setSelectedHubMeta] = useState<HubOption | null>(
    null
  );
  // Client scope (§D13). Empty = all clients (no filter sent). Options come
  // from the same providers source the shipments Import modal uses
  // (GET /v1/providers, searchable + paginated) — one client list everywhere.
  const providers = useProvidersDropdown(isOpen);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);

  // A client pitstop (hasStaff=false) only ever holds its owning client's
  // goods, so the client scope collapses to that one client: the picker shows
  // only them, pre-selected. Staffed Dash hubs keep the full providers list.
  const isClientPitstop =
    selectedHubMeta != null &&
    selectedHubMeta.hasStaff === false &&
    selectedHubMeta.clientId != null;
  const [drivers, setDrivers] = useState<DispatchDriver[]>([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Rider list source. "Recommended" filters the driver service by
  // tagNames=<hub name> (riders tagged as serving that hub); "All" is the
  // unfiltered list. Search applies to both.
  const [riderTab, setRiderTab] = useState<'RECOMMENDED' | 'ALL'>('ALL');
  // Selected riders carry phone alongside the snapshot fields. The phone
  // isn't sent to the BE (snapshot stays 3-key) — kept FE-local for the
  // persistent list display.
  type SelectedRider = DispatchRiderSnapshot & { phone?: string };
  const [selected, setSelected] = useState<Record<number, SelectedRider>>({});

  // Effective planner defaults (GET /v1/dispatch/config) — prefills the caps
  // input so ops sees the number they're overriding.
  const [config, setConfig] = useState<DispatchConfig | null>(null);
  // Max stops per batch override (string for the input; empty = use default).
  // softMaxStopsPerBatch is derived (max − 2) on submit, never shown to ops.
  const [maxStopsPerBatch, setMaxStopsPerBatch] = useState<string>('');

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setHubId('');
    setSelectedHubMeta(null);
    setSelectedClientIds([]);
    setSelected({});
    setSearch('');
    setDebouncedSearch('');
  }, [isOpen]);

  // Load the effective planner defaults each time the modal opens (env-driven,
  // so re-tunes show without a deploy) and prefill the max-stops input.
  // Non-fatal on failure — an empty field just means the BE uses its default.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    dispatchesAPI
      .config()
      .then((c) => {
        if (cancelled) return;
        setConfig(c);
        const n = Number(c?.maxStopsPerBatch);
        setMaxStopsPerBatch(
          String(
            Number.isFinite(n) && n > 0 ? n : DEFAULT_MAX_STOPS_PER_BATCH
          )
        );
      })
      .catch(() => {
        if (!cancelled) {
          setConfig(null);
          setMaxStopsPerBatch(String(DEFAULT_MAX_STOPS_PER_BATCH));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Origin NAME only (no code prefix) — the RECOMMENDED rider tab filters the
  // driver service by tagNames=<this name>. The hook's label cache keeps it
  // resolvable even after a later search narrows the option out of the list.
  const selectedHubName = useMemo(
    () =>
      hubSearch.options.find((h) => h.id === hubId)?.name ??
      hubSearch.labelFor(hubId),
    [hubSearch, hubId]
  );

  // Picking a hub jumps to its recommendations (that's the whole point of
  // the tab); clearing the hub falls back to All since there's no tag to
  // filter by. Ops can still switch tabs manually afterwards.
  useEffect(() => {
    setRiderTab(hubId ? 'RECOMMENDED' : 'ALL');
  }, [hubId]);

  // Fetch lives in the effect (not a bare callback) so the cleanup flag can
  // discard stale responses: picking a hub flips the tab to RECOMMENDED one
  // render after a fetch may already be in flight, and without cancellation
  // the slower unfiltered response would overwrite the recommended list.
  useEffect(() => {
    if (!isOpen) return;
    // Riders are hub-scoped — don't load anything until a hub is picked; the
    // list shows a "pick a hub" placeholder in the meantime.
    if (!hubId) {
      setDrivers([]);
      setDriversLoading(false);
      return;
    }
    let cancelled = false;
    setDriversLoading(true);
    dispatchDriversAPI
      .list({
        active: true,
        search: debouncedSearch || undefined,
        size: 100,
        ...(riderTab === 'RECOMMENDED' && selectedHubName
          ? { tagNames: selectedHubName }
          : {}),
      })
      .then((res) => {
        if (!cancelled) setDrivers(res.data ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        setDrivers([]);
        toast.error('Could not load drivers', {
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      })
      .finally(() => {
        if (!cancelled) setDriversLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, hubId, debouncedSearch, riderTab, selectedHubName]);

  const addDriver = (d: DispatchDriver) => {
    setSelected((prev) => ({
      ...prev,
      [d.id]: {
        id: d.id,
        code: d.code,
        name: driverDisplayName(d),
        phone: d.phone_number,
      },
    }));
  };

  const removeRider = (riderId: number) => {
    setSelected((prev) => {
      const next = { ...prev };
      delete next[riderId];
      return next;
    });
  };

  const selectedIds = useMemo(
    () => new Set(Object.keys(selected).map((k) => Number(k))),
    [selected]
  );
  const selectedCount = selectedIds.size;

  const canSubmit = !submitting && hubId !== '' && selectedCount > 0;

  // Effective default shown on the max-stops field — config value if usable,
  // else the hard-coded fallback (8).
  const defaultMaxStops = (() => {
    const n = Number(config?.maxStopsPerBatch);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_MAX_STOPS_PER_BATCH;
  })();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Persisted-preview model: this creates a PREVIEW dispatch with the
      // full plan persisted (batches/stops/items). Ops then reviews and
      // edits via PATCH, and commits via POST /:id/commit when ready.
      // Every dispatch is created as PRE_DISPATCH (product decision): the
      // planner also picks up imported items not yet hub-scanned, and the
      // commit gate holds until every shipment is fully scanned.
      // Max-stops override: send the hard cap + a soft cap 2 below it (the
      // planner's "prefer under this" target). Soft is derived, not shown to
      // ops. Empty input → omit both → BE falls back to DISPATCH_CONFIG.
      const maxStops = maxStopsPerBatch.trim()
        ? Number(maxStopsPerBatch)
        : NaN;
      const stopsCaps =
        Number.isFinite(maxStops) && maxStops > 0
          ? {
              maxStopsPerBatch: maxStops,
              softMaxStopsPerBatch: Math.max(1, maxStops - 2),
            }
          : {};

      const dispatch = await dispatchesAPI.preview({
        hubId,
        // Strip the FE-only `phone` field — backend only wants {id, code, name}.
        riders: Object.values(selected).map(({ id, code, name }) => ({
          id,
          code,
          name,
        })),
        mode: 'PRE_DISPATCH',
        ...(selectedClientIds.length > 0
          ? { clientIds: selectedClientIds }
          : {}),
        ...stopsCaps,
      });
      const plannedBatches = dispatch.batches.filter(
        (b) => b.status === 'PLANNED'
      ).length;
      const deferredBatches = dispatch.batches.filter(
        (b) => b.status === 'DEFERRED'
      ).length;
      toast.success('Preview created', {
        description:
          `${dispatch.code} · ${plannedBatches} batch${plannedBatches === 1 ? '' : 'es'}` +
          (deferredBatches > 0 ? `, ${deferredBatches} deferred` : '') +
          ` · review & commit when ready`,
      });
      onCreated(dispatch);
      onClose();
    } catch (err) {
      toast.error('Could not create preview', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-send-plane-line" />}
      iconTone="primary"
      title="Create dispatch preview"
      subtitle="Pick the hub, client scope, and riders on shift. The plan also includes imported items not yet scanned at the hub (commit stays blocked until every shipment is fully scanned) and retrieval pickups bound for this hub, planned as Pickup stops on the same round."
      size="2xl"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-between w-full">
          <span className="text-xs text-text-sub-600">
            {selectedCount} rider{selectedCount === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2">
            <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? 'Building plan…' : 'Create preview'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="dispatch-hub" className={labelCls}>
              Hub / Pitstop client
            </label>
            <SearchSelect
              // Dash hubs first, client pitstops after, under labeled
              // section headers (SearchSelect renders one per `group` run).
              options={[...hubSearch.options]
                .sort((a, b) => Number(isDashHub(b)) - Number(isDashHub(a)))
                .map((h) => ({
                  value: h.id,
                  label: `${h.code} · ${h.name}${
                    h.clientName ? ` — ${h.clientName}` : ''
                  }`,
                  group: isDashHub(h) ? 'Hub Dash' : 'Pitstop Client',
                }))}
              value={hubId || undefined}
              selectedLabelFallback={hubId ? hubSearch.labelFor(hubId) : undefined}
              onChange={(v) => {
                const id = (v as string | undefined) ?? '';
                setHubId(id);
                const meta =
                  hubSearch.options.find((h) => h.id === id) ?? null;
                setSelectedHubMeta(meta);
                // Client scope follows the origin: a client pitstop locks the
                // scope to its owner; picking/clearing anything else resets
                // back to "all clients".
                setSelectedClientIds(
                  meta && meta.hasStaff === false && meta.clientId != null
                    ? [meta.clientId]
                    : []
                );
              }}
              onSearchChange={hubSearch.setSearch}
              disableLocalFilter
              loading={hubSearch.loading}
              placeholder="Search or pick a hub / pitstop…"
              disabled={submitting}
              emptyMessage="No matching location"
            />
          </div>

          {/* Client scope picker (§D13) — same providers source as the
              shipments Import modal, but multi-select. Empty selection =
              all clients (no filter sent). */}
          <div>
            <label className={labelCls}>
              Clients{' '}
              <span className="normal-case font-medium tracking-normal text-text-soft-400">
                {isClientPitstop
                  ? '— pitstop client, terkunci ke pemiliknya'
                  : '— none selected = all clients'}
              </span>
            </label>
            <SearchSelect
              multiple
              placeholder="Search or pick clients…"
              // A client pitstop's scope is its owning client, nothing else —
              // the one option is pre-selected by the hub picker's onChange.
              options={
                isClientPitstop
                  ? [
                      {
                        value: selectedHubMeta!.clientId!,
                        label:
                          selectedHubMeta!.clientName ??
                          `Client ${selectedHubMeta!.clientId}`,
                      },
                    ]
                  : providers.options.map((p) => ({
                      value: p.id,
                      label: p.name,
                    }))
              }
              value={selectedClientIds}
              onChange={(next) => setSelectedClientIds(next.map(Number))}
              onSearchChange={
                isClientPitstop ? undefined : providers.setSearch
              }
              disableLocalFilter
              loading={!isClientPitstop && providers.loading}
              hasMore={!isClientPitstop && providers.hasMore}
              onLoadMore={isClientPitstop ? undefined : providers.loadMore}
              allowClear={!isClientPitstop}
              disabled={submitting || isClientPitstop}
              emptyMessage={providers.error ?? 'No clients found'}
            />
            {!isClientPitstop && providers.error && (
              <p className="mt-1 text-[11px] text-error-base">
                {providers.error}
              </p>
            )}
          </div>
        </div>

        {/* Per-run cap override. Only max-stops is exposed; the matching soft
            cap is derived (max − 2) on submit. Empty = hub default. */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="dispatch-max-stops" className={labelCls}>
              Max stop (delivery) per batch
              <span className="normal-case font-medium tracking-normal text-text-soft-400">
                {' '}
                — default {defaultMaxStops}
              </span>
            </label>
            <input
              id="dispatch-max-stops"
              type="number"
              min={1}
              value={maxStopsPerBatch}
              onChange={(e) => setMaxStopsPerBatch(e.target.value)}
              placeholder={String(defaultMaxStops)}
              className={inputCls}
              disabled={submitting}
            />
            <p className="mt-1 text-[11px] text-text-soft-400">
              Batas maksimum stop per batch. Kosongkan untuk pakai default hub.
            </p>
          </div>
        </div>

        {/* Two-column rider picker: search/add on the left stays anchored
            while the right column accumulates the selection. Equal heights
            so scrolling is balanced when either side grows. The rider list is
            hub-scoped, so before a hub is picked it shows a placeholder. */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* LEFT — Add riders */}
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <span className={`${labelCls} mb-0`}>Add riders</span>
              <span className="text-xs text-text-sub-600">
                {driversLoading ? '…' : `${drivers.length} matched`}
              </span>
            </div>
            {/* Source tabs — Recommended (drivers tagged with the selected
                hub's name) vs All (unfiltered). Recommended needs a hub. */}
            <div className="flex items-center -mb-px border-b border-stroke-soft-200 mb-2">
              <button
                type="button"
                onClick={() => setRiderTab('RECOMMENDED')}
                disabled={submitting || !hubId}
                title={!hubId ? 'Select a hub first' : undefined}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold tracking-tight transition-colors border-b-2 disabled:cursor-not-allowed ${
                  riderTab === 'RECOMMENDED'
                    ? 'border-accent text-text-strong-950'
                    : 'border-transparent text-text-sub-600 hover:text-text-strong-950 disabled:text-text-disabled-300 disabled:hover:text-text-disabled-300'
                }`}
              >
                <i className="ri-sparkling-line text-[13px]" aria-hidden="true" />
                Dedicated Riders
              </button>
              <button
                type="button"
                onClick={() => setRiderTab('ALL')}
                disabled={submitting}
                className={`inline-flex items-center px-3 py-1.5 text-[12px] font-semibold tracking-tight transition-colors border-b-2 ${
                  riderTab === 'ALL'
                    ? 'border-accent text-text-strong-950'
                    : 'border-transparent text-text-sub-600 hover:text-text-strong-950'
                }`}
              >
                All riders
              </button>
            </div>
            <div className="relative mb-2">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-text-soft-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, code, or phone…"
                className={`${inputCls} pl-9`}
                disabled={submitting || !hubId}
              />
            </div>
            <div className="flex-1 min-h-[280px] max-h-[420px] overflow-y-auto rounded-lg border border-stroke-soft-200 divide-y divide-stroke-soft-200">
              {!hubId ? (
                <div className="flex h-full items-center justify-center px-3 py-6 text-center text-xs text-text-sub-600">
                  Pilih hub dulu untuk menampilkan rider.
                </div>
              ) : driversLoading ? (
                <div className="px-3 py-4 text-center text-xs text-text-sub-600">
                  Loading drivers…
                </div>
              ) : drivers.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-text-sub-600">
                  {riderTab === 'RECOMMENDED'
                    ? search
                      ? 'No matches among riders tagged for this hub.'
                      : `No riders tagged "${selectedHubName}" yet — check All riders.`
                    : search
                    ? 'No matches.'
                    : 'Type to search drivers.'}
                </div>
              ) : (
                drivers.map((d) => {
                  const isSelected = selectedIds.has(d.id);
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => addDriver(d)}
                      disabled={submitting || isSelected}
                      className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left transition-colors ${
                        isSelected
                          ? 'bg-bg-weak-50 cursor-default'
                          : 'hover:bg-bg-weak-50 cursor-pointer'
                      }`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] text-text-strong-950 tracking-tight truncate">
                          {driverDisplayName(d)}
                        </span>
                        <span className="flex items-center gap-2 font-mono text-[11px] text-text-sub-600 tabular-nums">
                          <span>{d.code}</span>
                          {d.phone_number && (
                            <>
                              <span aria-hidden="true">·</span>
                              <span>{d.phone_number}</span>
                            </>
                          )}
                        </span>
                      </span>
                      {isSelected ? (
                        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-text-soft-400 shrink-0">
                          Added
                        </span>
                      ) : (
                        <i className="ri-add-line text-[16px] text-accent shrink-0" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* RIGHT — Selected riders (persistent across search changes) */}
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <span className={`${labelCls} mb-0`}>
                Selected ({selectedCount})
              </span>
              {selectedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setSelected({})}
                  disabled={submitting}
                  className="text-[11px] font-medium text-text-sub-600 hover:text-text-strong-950 transition-colors disabled:opacity-50"
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="flex-1 min-h-[280px] max-h-[420px] overflow-y-auto rounded-lg border border-stroke-soft-200 bg-bg-weak-50/40 divide-y divide-stroke-soft-200">
              {selectedCount === 0 ? (
                <div className="flex h-full items-center justify-center px-3 py-6 text-center text-xs text-text-sub-600">
                  No riders selected yet.<br />Use the search on the left to add.
                </div>
              ) : (
                Object.values(selected).map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 bg-white"
                  >
                    <div className="min-w-0">
                      <span className="block text-[13px] font-medium text-text-strong-950 truncate">
                        {r.name}
                      </span>
                      <span className="flex items-center gap-2 font-mono text-[11px] text-text-sub-600 tabular-nums">
                        <span>{r.code}</span>
                        {r.phone && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span>{r.phone}</span>
                          </>
                        )}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRider(r.id)}
                      disabled={submitting}
                      aria-label={`Remove ${r.name}`}
                      title={`Remove ${r.name}`}
                      className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-lg text-text-soft-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    >
                      <i className="ri-close-line text-[16px]" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </FormModal>
  );
};

export default CreateDispatchModal;
